<template>
  <div class="dashboard-container" v-loading="workItemStore.dashboardLoading && tasksLoading">
    <!-- 헤더: 제목 + 요약 칩 -->
    <div class="dash-header">
      <span class="dash-title">내 업무</span>
      <div class="chips">
        <span class="chip chip-danger">지연 {{ counts.overdue }}</span>
        <span class="chip chip-warning">오늘 {{ counts.today }}</span>
        <span class="chip chip-info">예정 {{ counts.upcoming }}</span>
        <span class="chip chip-purple chip-click" @click="$router.push('/approval/inbox')">
          결재 대기 {{ approvalStore.myPending.length }}
        </span>
      </div>
    </div>

    <!-- 즐겨찾기(핀 고정) 메뉴는 2026-08-08부로 상단 네비게이션(FavoritesMenu.vue)으로 이동함.
         다른 화면으로 이동하면 이 위젯이 사라져 불편하다는 사이먼 피드백 반영 — 이 화면 전용 위젯은 제거. -->

    <!-- 오늘 일정 서브라인 (회사·부서 달력 요약) -->
    <div class="today-strip" @click="$router.push('/calendar')">
      <span class="strip-label">📅 오늘</span>
      <span v-if="todayContext.events.length" class="strip-item muted">{{ eventsText }}</span>
      <span v-else class="strip-item muted">일정 없음</span>
      <span class="strip-more">달력 전체 보기 ›</span>
    </div>

    <!-- 통합 할 일 (개인 일정 + 시스템 배정 업무) — 최상단 배치 -->
    <el-card shadow="never" class="section-card">
      <div class="section-head">
        <span>할 일</span>
        <el-radio-group v-model="viewMode" size="small">
          <el-radio-button label="urgency">마감일별</el-radio-button>
          <el-radio-button label="main_request">메인오더별</el-radio-button>
        </el-radio-group>
      </div>

      <!-- 긴급도별 (기본): 개인+업무 통합 -->
      <template v-if="viewMode === 'urgency'">
        <div v-for="sec in sections" :key="sec.key" v-show="sec.items.length" class="task-section">
          <div class="section-label" :class="sec.cls">{{ sec.label }} {{ sec.items.length }}</div>
          <div v-for="row in sec.items" :key="row.key" class="task-row">
            <el-checkbox class="row-check" :model-value="false" @change="completeRow(row)" />
            <el-tag size="small" :type="row.kind === 'task' ? 'success' : 'warning'" class="row-tag">
              {{ row.tag }}
            </el-tag>
            <div class="row-body" @click="openRow(row)">
              <span class="row-title">{{ row.title || '(제목 없음)' }}</span>
              <span v-if="row.sub" class="row-sub">{{ row.sub }}</span>
            </div>
            <span class="row-date" :class="{ danger: row.overdue }">{{ row.dueLabel }}</span>
          </div>
        </div>
        <div v-if="!hasAnyRows" class="empty-line">할 일이 없습니다 — 아래에서 추가해보세요.</div>

        <!-- 완료된 개인 일정 (토글) -->
        <el-button v-if="completedTasks.length" text size="small" class="done-toggle" @click="showCompleted = !showCompleted">
          {{ showCompleted ? '완료 숨기기' : '완료 ' + completedTasks.length + ' 보기' }}
        </el-button>
        <div v-if="showCompleted" class="task-section">
          <div v-for="t in completedTasks" :key="t.id" class="task-row">
            <el-tag size="small" type="info" class="row-tag">개인</el-tag>
            <div class="row-body" @click="openTaskDetail(t)">
              <span class="row-title done">{{ t.title || '(제목 없음)' }}</span>
            </div>
            <span class="row-date">{{ t.end_date }}</span>
          </div>
        </div>

        <!-- 빠른 추가 (개인 일정) -->
        <div class="quick-add">
          <el-input
            v-model="newTitle"
            placeholder="할 일 추가"
            clearable
            maxlength="200"
            class="qa-title"
            @keydown.enter.stop.prevent="addTask"
          />
          <el-date-picker
            v-model="newDate"
            type="date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            :clearable="false"
            class="qa-date"
            placeholder="마감일"
          />
          <el-button type="primary" :loading="adding" class="qa-btn" @click="addTask">추가</el-button>
        </div>
      </template>

      <!-- 메인오더별 -->
      <template v-else>
        <el-empty v-if="byMainRequest.length === 0" description="할 일이 없습니다" :image-size="60" />
        <el-collapse v-else v-model="expandedRequests">
          <el-collapse-item
            v-for="group in byMainRequest"
            :key="group.main_request?.id || 'general'"
            :name="group.main_request?.id || 'general'"
          >
            <template #title>
              <span class="mr-group-title">
                <el-tag v-if="group.main_request" size="small">
                  {{ group.main_request.request_number }}
                </el-tag>
                <span v-if="group.main_request?.brand_name" class="mr-brand">
                  {{ group.main_request.brand_name }}
                </span>
                <span v-if="!group.main_request" class="mr-general">기타 업무</span>
                <el-badge :value="group.items.length" type="info" class="mr-count" />
              </span>
            </template>
            <WorkItemList :items="group.items" @complete="handleWorkComplete" />
          </el-collapse-item>
        </el-collapse>
      </template>
    </el-card>

    <!-- 결재 대기 -->
    <el-card v-if="approvalStore.myPending.length" shadow="never" class="section-card">
      <div class="section-head">
        <span class="head-purple">🖋 결재 대기 {{ approvalStore.myPending.length }}건</span>
        <el-button text size="small" @click="$router.push('/approval/inbox')">결재함 전체</el-button>
      </div>
      <div v-for="row in pendingPreview" :key="row.id" class="approval-row">
        <el-tag size="small">{{ docLabel(row.document_type) }}</el-tag>
        <span class="approval-title" @click="openApproval(row)">{{ row.title }}</span>
        <span class="approval-requester">{{ row.requester_name }}</span>
        <el-button size="small" type="primary" plain @click="openApproval(row)">바로 결재</el-button>
      </div>
    </el-card>

    <TaskDetailDialog v-model="showDetail" :task="detailTask" @changed="fetchTasks" />
    <ApprovalProcessDialog
      v-model="approvalDialogVisible"
      :approval-id="selectedApprovalId"
      :detail-path="selectedDetailPath"
      @processed="onApprovalProcessed"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import dayjs from 'dayjs'
import axiosInstance from '@/api/axios'
import { useWorkItemStore } from '@/stores/workItemStore'
import { useApprovalStore } from '@/stores/approvalStore'
import WorkItemList from '@/components/workitem/WorkItemList.vue'
import TaskDetailDialog from '@/components/TaskDetailDialog.vue'
import ApprovalProcessDialog from '@/components/approval/ApprovalProcessDialog.vue'

const router = useRouter()
const workItemStore = useWorkItemStore()
const approvalStore = useApprovalStore()

// 즐겨찾기(핀 고정) 메뉴 관련 상태/로직은 2026-08-08부로 상단 네비게이션의
// FavoritesMenu.vue로 이전함 (이 화면 전용이 아니라 전역에서 접근 가능해야 하므로).

const todayStr = dayjs().format('YYYY-MM-DD')
const viewMode = ref('urgency')
const expandedRequests = ref([])
const byMainRequest = computed(() => workItemStore.byMainRequest)

// ===== 개인 일정 (Task) =====
const tasks = ref([])
const tasksLoading = ref(false)
const showCompleted = ref(false)
const showDetail = ref(false)
const detailTask = ref(null)
const newTitle = ref('')
const newDate = ref(todayStr)
const adding = ref(false)

const normalizeList = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const fetchTasks = async () => {
  tasksLoading.value = true
  try {
    const res = await axiosInstance.get('tasks/', { params: { mine: 1 } })
    tasks.value = normalizeList(res.data)
  } catch (err) {
    console.error('내 일정 조회 실패:', err)
  } finally {
    tasksLoading.value = false
  }
}

const incompleteTasks = computed(() => tasks.value.filter((t) => t.status !== 'completed'))
const completedTasks = computed(() =>
  tasks.value.filter((t) => t.status === 'completed').sort((a, b) => (a.end_date < b.end_date ? 1 : -1))
)

const openTaskDetail = (t) => {
  detailTask.value = t
  showDetail.value = true
}

const addTask = async () => {
  const title = newTitle.value.trim()
  if (!title) {
    ElMessage.warning('할 일을 입력하세요.')
    return
  }
  const due = newDate.value || todayStr
  adding.value = true
  try {
    await axiosInstance.post('tasks/', { title, start_date: due, end_date: due, status: 'pending' })
    newTitle.value = ''
    newDate.value = todayStr
    await fetchTasks()
  } catch (err) {
    console.error('내 일정 추가 실패:', err)
    ElMessage.error('추가에 실패했습니다.')
  } finally {
    adding.value = false
  }
}

// ===== 통합 행 빌더 =====
const taskRow = (t) => ({
  key: `task-${t.id}`,
  kind: 'task',
  id: t.id,
  title: t.title,
  tag: '개인',
  sub: '',
  dueLabel: t.end_date ? t.end_date.substring(5) : '',
  overdue: t.end_date < todayStr,
  raw: t,
})

const workRow = (item) => ({
  key: `work-${item.id}`,
  kind: 'work',
  id: item.id,
  title: item.title,
  tag: item.task_type_display || '업무',
  sub: item.brand_name || '',
  dueLabel: item.due_date
    ? item.due_date.substring(5) +
      (item.days_remaining !== null && item.days_remaining !== undefined
        ? item.days_remaining >= 0 ? ` D-${item.days_remaining}` : ` D+${Math.abs(item.days_remaining)}`
        : '')
    : '기한없음',
  overdue: !!item.is_overdue,
  raw: item,
})

const byDue = (a, b) => ((a.raw.end_date || a.raw.due_date || '') < (b.raw.end_date || b.raw.due_date || '') ? -1 : 1)

const sections = computed(() => {
  const overdue = [
    ...incompleteTasks.value.filter((t) => t.end_date < todayStr).map(taskRow),
    ...workItemStore.overdueItems.map(workRow),
  ].sort(byDue)
  const today = [
    ...incompleteTasks.value.filter((t) => t.end_date === todayStr).map(taskRow),
    ...workItemStore.todayItems.map(workRow),
  ]
  const upcoming = [
    ...incompleteTasks.value.filter((t) => t.end_date > todayStr).map(taskRow),
    ...workItemStore.upcomingItems.map(workRow),
  ].sort(byDue)
  const noDate = workItemStore.noDateItems.map(workRow)
  return [
    { key: 'overdue', label: '지연', cls: 'overdue', items: overdue },
    { key: 'today', label: '오늘', cls: 'today', items: today },
    { key: 'upcoming', label: '예정', cls: 'upcoming', items: upcoming },
    { key: 'no_date', label: '기한없음', cls: 'nodate', items: noDate },
  ]
})

const counts = computed(() => ({
  overdue: sections.value[0].items.length,
  today: sections.value[1].items.length,
  upcoming: sections.value[2].items.length,
}))
const hasAnyRows = computed(() => sections.value.some((s) => s.items.length > 0))

const completeRow = async (row) => {
  if (row.kind === 'task') {
    try {
      await axiosInstance.patch(`tasks/${row.id}/`, { status: 'completed' })
      ElMessage.success('완료했습니다.')
      await fetchTasks()
    } catch (err) {
      console.error('완료 처리 실패:', err)
      ElMessage.error('처리에 실패했습니다.')
      fetchTasks()
    }
  } else {
    handleWorkComplete(row.raw)
  }
}

const handleWorkComplete = async (item) => {
  try {
    await ElMessageBox.confirm(`"${item.title}" 완료 처리하시겠습니까?`, '업무 완료', {
      confirmButtonText: '완료',
      cancelButtonText: '취소',
      type: 'info',
    })
    await workItemStore.completeItem(item.id)
    ElMessage.success('완료 처리되었습니다.')
  } catch {
    // 사용자 취소
  }
}

const openRow = (row) => {
  if (row.kind === 'task') {
    openTaskDetail(row.raw)
  } else if (row.raw.detail_url) {
    router.push(row.raw.detail_url)
  }
}

// ===== 결재 대기 =====
const documentTypeLabels = {
  sample_request: '샘플요청',
  expense: '지출결의서',
  quotation: '견적서',
  material_order: '자재발주',
  main_request: '메인오더',
  leave: '휴가',
  daily_cash_report: '자금일보',
}
const documentRoutes = {
  sample_request: '/sample-requests',
  expense: '/expenses',
  quotation: '/quotations',
  material_order: '/material-orders',
  main_request: '/main-requests',
  daily_cash_report: '/finance/daily-reports',
}
const dialogDocumentTypes = new Set(['leave', 'sample_request', 'daily_cash_report'])

const docLabel = (type) => documentTypeLabels[type] || type
const pendingPreview = computed(() => approvalStore.myPending.slice(0, 5))

const approvalDialogVisible = ref(false)
const selectedApprovalId = ref(null)
const selectedDetailPath = ref('')

const openApproval = (row) => {
  if (dialogDocumentTypes.has(row.document_type)) {
    selectedApprovalId.value = row.id
    const basePath = documentRoutes[row.document_type]
    selectedDetailPath.value = basePath && row.object_id ? `${basePath}/${row.object_id}` : ''
    approvalDialogVisible.value = true
    return
  }
  const basePath = documentRoutes[row.document_type]
  if (basePath && row.object_id) {
    router.push(`${basePath}/${row.object_id}`)
  } else {
    router.push('/approval/inbox')
  }
}

const onApprovalProcessed = () => {
  approvalStore.fetchMyPending()
}

// ===== 오늘 일정 스트립 =====
const todayContext = ref({ events: [] })

const fetchTodayContext = async () => {
  try {
    const res = await axiosInstance.get('dashboard/today-context/')
    todayContext.value = {
      events: res.data?.events || [],
    }
  } catch (err) {
    console.error('오늘 일정 조회 실패:', err)
  }
}

const eventsText = computed(() =>
  todayContext.value.events
    .map((e) => (e.calendar_name ? `${e.title} (${e.calendar_name})` : e.title))
    .join(' · ')
)

onMounted(() => {
  workItemStore.fetchDashboard()
  fetchTasks()
  approvalStore.fetchMyPending()
  fetchTodayContext()
})
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
}

/* ===== 헤더 + 칩 ===== */
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.dash-title {
  font-size: 16px;
  font-weight: 700;
}
.chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.chip {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
}
.chip-danger { background: #fef0f0; color: #c45656; }
.chip-warning { background: #fdf6ec; color: #b88230; }
.chip-info { background: #ecf5ff; color: #337ecc; }
.chip-purple { background: #f4f0fc; color: #6f42c1; }
.chip-click { cursor: pointer; }

/* 즐겨찾기(핀 고정) 메뉴 스타일은 2026-08-08부로 FavoritesMenu.vue로 이전함. */

/* ===== 오늘 일정 스트립 ===== */
.today-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: -6px;
  padding: 0 2px 10px;
  cursor: pointer;
  font-size: 13px;
}
.today-strip:hover .strip-more {
  color: #909399;
}
.strip-label {
  font-weight: 600;
  color: #606266;
  flex-shrink: 0;
}
.strip-item { color: #303133; }
.strip-item.muted { color: #909399; }
.strip-more {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 12px;
  color: #c0c4cc;
}

/* ===== 섹션 카드 공통 ===== */
.section-card {
  margin-bottom: 12px;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
}
.head-purple { color: #6f42c1; }

/* ===== 결재 대기 ===== */
.approval-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #f5f7fa;
}
.approval-row:last-child { border-bottom: none; }
.approval-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.approval-requester {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

/* ===== 통합 할 일 리스트 ===== */
.task-section { margin-bottom: 6px; }
.section-label {
  font-size: 12px;
  font-weight: 700;
  margin: 10px 0 4px;
}
.section-label.overdue { color: #f56c6c; }
.section-label.today { color: #e6a23c; }
.section-label.upcoming { color: #409eff; }
.section-label.nodate { color: #909399; }

.task-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 2px;
  border-bottom: 1px solid #f5f7fa;
}
.row-check { flex-shrink: 0; }
.row-tag { flex-shrink: 0; }
.row-body {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.row-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row-title.done {
  text-decoration: line-through;
  color: #909399;
  font-weight: 400;
}
.row-sub {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}
.row-date {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}
.row-date.danger { color: #f56c6c; font-weight: 600; }

.empty-line {
  font-size: 13px;
  color: #909399;
  padding: 12px 0;
}
.done-toggle { margin-top: 4px; }

/* 빠른 추가 */
.quick-add {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.qa-title { flex: 1; }
.qa-date { width: 140px; flex-shrink: 0; }

/* 메인오더별 */
.mr-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mr-brand { color: #606266; font-size: 13px; }
.mr-general { color: #909399; font-size: 13px; }
.mr-count { margin-left: 4px; }

/* ===== 모바일 ===== */
@media (max-width: 768px) {
  .dashboard-container { padding: 12px; }
  .today-strip .strip-more { margin-left: 0; width: 100%; text-align: right; }
  .quick-add { flex-direction: column; align-items: stretch; }
  .qa-date { width: 100%; }
  .qa-btn { width: 100%; }
  .approval-requester { display: none; }
}
</style>
